import { Button, Icon, useBoolean, useBreakpointValue } from '@chakra-ui/react';
import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useState } from 'react';
import { MdOutlineNotificationsNone } from 'react-icons/md';
import { useLocation } from 'react-router-dom';

import {
  CustomerNotification,
  useCustomerNotificationsQuery,
  useDismissCustomerNotificationMutation,
  useMarkCustomerNotificationAsSeenMutation,
  useMarkCustomerNotificationsAsSeenMutation,
} from '../../../gql/generated/graphql';
import { NEW_DEAL_VALUE, dashboardDealInfoAtom } from '../RootDashboard';
import DesktopNotifications from './DesktopNotifications';
import MobileNotifications from './MobileNotifications';
import UnreadCountBubble from './UnreadCountBubble';
import NotificationModals from './notificationModals/NotificationModals';

export const unseenNotificationCountAtom = atom(0);
export const notificationsDrawerOpenAtom = atom(false);

type Props = {
  showBell?: boolean;
};

const Notifications = ({ showBell = true }: Props) => {
  const { pathname } = useLocation();
  const dealId = useAtomValue(dashboardDealInfoAtom);
  const isNewDeal = dealId?.id === NEW_DEAL_VALUE.id;
  const isMobile = useBreakpointValue({ base: true, md: false });
  const [isOpen, setDrawerOpen] = useAtom(notificationsDrawerOpenAtom);
  const onClose = useCallback(() => setDrawerOpen(false), [setDrawerOpen]);
  const onOpen = useCallback(() => setDrawerOpen(true), [setDrawerOpen]);
  const onToggle = useCallback(() => setDrawerOpen((prev) => !prev), [setDrawerOpen]);
  const [showDismissed, { off: hideDismissed, toggle: toggleShowDismissed }] = useBoolean();
  const setUnseenNotificationCount = useSetAtom(unseenNotificationCountAtom);

  const [unseenNotifications, setUnseenNotifications] = useState<CustomerNotification[]>([]);
  const [notDismissedNotifications, setNotDismissedNotifications] = useState<
    CustomerNotification[]
  >([]);
  const [dismissedNotifications, setDismissedNotifications] = useState<CustomerNotification[]>([]);
  const [openNotificationModal, setOpenNotificationModal] = useState<string | null>(null);

  const unseenCount = unseenNotifications.length;

  useEffect(() => {
    setUnseenNotificationCount(unseenCount);
  }, [unseenCount, setUnseenNotificationCount]);

  const handleSetNotifications = (notifications: CustomerNotification[] = []) => {
    setUnseenNotifications(notifications.filter((notification) => !notification.seen_at));
    setNotDismissedNotifications(
      notifications.filter((notification) => !notification.dismissed_at),
    );
    setDismissedNotifications(notifications.filter((notification) => notification.dismissed_at));
  };

  const [markNotificationsAsSeen] = useMarkCustomerNotificationsAsSeenMutation({
    onCompleted: ({ markCustomerNotificationsAsSeen }) => {
      handleSetNotifications(markCustomerNotificationsAsSeen as CustomerNotification[]);
    },
  });

  useCustomerNotificationsQuery({
    variables: { dealId: dealId?.id ?? '', isCobuyer: dealId?.isCobuyer ?? false },
    skip: !dealId?.id || isNewDeal,
    onCompleted: ({ customerNotifications }) => {
      handleSetNotifications(customerNotifications as CustomerNotification[]);
    },
  });

  const [dismissCustomerNotificationMutation] = useDismissCustomerNotificationMutation({
    onCompleted: ({ dismissCustomerNotification }) => {
      handleSetNotifications(dismissCustomerNotification as CustomerNotification[]);
    },
  });

  const [markNotificationAsSeen] = useMarkCustomerNotificationAsSeenMutation({
    onCompleted: ({ markCustomerNotificationAsSeen }) => {
      handleSetNotifications(markCustomerNotificationAsSeen as CustomerNotification[]);
    },
  });

  useEffect(() => {
    if (isNewDeal) {
      setUnseenNotifications([]);
      setNotDismissedNotifications([]);
      setDismissedNotifications([]);
    }
  }, [isNewDeal]);

  useEffect(() => {
    if (isOpen && unseenCount > 0) {
      if (!dealId?.id || isNewDeal) {
        return;
      }

      markNotificationsAsSeen({
        variables: { dealId: dealId.id, isCobuyer: dealId?.isCobuyer ?? false },
      });
    }
  }, [isOpen, unseenCount, dealId?.id, isNewDeal, markNotificationsAsSeen]);

  useEffect(() => {
    if (isOpen) {
      onClose();
    }
  }, [pathname, onClose]);

  const handleToggle = () => {
    onToggle();
    hideDismissed();
  };

  const NotificationBell = (
    <Button
      aria-label="Open notifications"
      position="relative"
      variant="link"
      mr={3}
      onClick={handleToggle}
    >
      <UnreadCountBubble count={unseenCount} position="absolute" top={-1} right={-2} />
      <Icon color="taupeGray" as={MdOutlineNotificationsNone} boxSize="26px" />
    </Button>
  );

  const handleNotificationModalClose = async () => {
    const notificationWithCallToAction = notDismissedNotifications.find((not) =>
      not.call_to_actions?.find((cta) => cta?.action === openNotificationModal),
    );

    setOpenNotificationModal(null);

    if (!dealId?.id || !notificationWithCallToAction) {
      return;
    }

    if (!notificationWithCallToAction.auto_mark_as_seen && !notificationWithCallToAction.seen_at) {
      await markNotificationAsSeen({
        variables: {
          id: notificationWithCallToAction.id as string,
          dealId: dealId.id,
          isCobuyer: dealId?.isCobuyer ?? false,
        },
      });
    }

    if (!notificationWithCallToAction.dismissed_at) {
      await dismissCustomerNotificationMutation({
        variables: {
          id: notificationWithCallToAction.id as string,
          dealId: dealId.id,
          isCobuyer: dealId?.isCobuyer ?? false,
        },
      });
    }
  };

  return (
    <>
      {isMobile ? (
        <>
          {showBell && NotificationBell}
          <MobileNotifications
            isOpen={isOpen}
            onClose={onClose}
            notDismissedNotifications={notDismissedNotifications}
            dismissedNotifications={dismissedNotifications}
            dismissNotification={dismissCustomerNotificationMutation}
            showDismissed={showDismissed}
            toggleShowDismissed={toggleShowDismissed}
            setOpenNotificationModal={setOpenNotificationModal}
          />
        </>
      ) : (
        <DesktopNotifications
          isOpen={isOpen}
          onOpen={onOpen}
          onClose={onClose}
          notDismissedNotifications={notDismissedNotifications}
          dismissedNotifications={dismissedNotifications}
          dismissNotification={dismissCustomerNotificationMutation}
          showDismissed={showDismissed}
          toggleShowDismissed={toggleShowDismissed}
          setOpenNotificationModal={setOpenNotificationModal}
        >
          {NotificationBell}
        </DesktopNotifications>
      )}
      <NotificationModals
        openNotificationModal={openNotificationModal}
        onClose={handleNotificationModalClose}
      />
    </>
  );
};

export default Notifications;
